import React, { useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import {
  clearAccountInfo,
  getAccountInfo,
  setAccountInfo,
  setLanguage,
  setScheme
} from '@/utils/localStorage';
import { useLocalStorageState } from 'ahooks';
import { Button, Checkbox, Divider, Form, Input, message } from 'antd';
import { decrypt, encrypt } from '@/utils/aes';
import { login } from '@/api/login';

import AppleLogo from '@/assets/images/apple-logo.svg';
import GoogleLogo from '@/assets/images/google-logo.svg';
import MicrosoftLogo from '@/assets/images/microsoft-logo.svg';

import { ReactComponent as EmailSvg } from '@/assets/images/icons/email.svg';
import { ReactComponent as PasswordSvg } from '@/assets/images/icons/password.svg';

import './index.less';
import Agreement from '@/components/agreement';
import LogoComp from '@/components/logo';
import WeixinTip from '@/components/weixinTip';
import { THIRD_URL } from '@/utils/constant';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch } from '@/store';
import { updateLanguage } from '@/store/module/account';

interface IProps {
  children?: ReactNode;
}
const SignIn: FC<IProps> = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [visiable, setVisibale] = useState(false);
  const [isExpire] = useState(false);
  const { code, error } = queryString.parse(useLocation().search);
  if (code) {
    setScheme(code as string);
    const data: any = JSON.parse(
      decrypt(decodeURIComponent(code as string)) || '{}'
    );
    console.log('data?.language', data?.language);
    const locale = data?.language?.replace('_', '-');
    if (locale) {
      dispatch(updateLanguage(locale));
      setLanguage(locale);
    }
  }
  const [remember, setRemember] = useLocalStorageState<boolean | undefined>(
    'REMEMBER',
    {
      defaultValue: true
    }
  );

  const [form] = Form.useForm();
  const email = Form.useWatch('email', form);
  const password = Form.useWatch('password', form);

  const onFinishFailed = ({ errorFields }: any) => {
    message.error(errorFields[0].errors[0], 1.5);
  };

  const onFinish = async () => {
    setLoading(true);
    if (remember === true) {
      setAccountInfo('username', encrypt(email));
      setAccountInfo('password', encrypt(password));
    } else {
      clearAccountInfo();
    }
    try {
      const { data, success, msg }: any = await login({
        password: encrypt(password),
        username: email
      });
      if (success) {
        navigate(`/auth/success?token=${data?.baseToken}&type=password`);
      } else {
        message.error(msg);
      }
    } catch (error) {
      const msg = (error as any)?.response?.data?.msg;
      console.log((error as any)?.response);
      msg && message.error(msg);
    }
    setLoading(false);
  };

  const thirdLogin = async (thirdUrl: any) => {
    const callback = location.origin + '/auth/success';
    location.href = thirdUrl + `&state=__${callback}`;
  };

  const init = async () => {
    const initEmail = getAccountInfo('username');
    const initPassword = getAccountInfo('password');
    if (!!initEmail && !!initPassword) {
      form.setFieldsValue({
        email: decrypt(initEmail),
        password: decrypt(initPassword)
      });
      setRemember(true);
    }
  };

  useEffect(() => {
    init();
  }, []);
  useEffect(() => {
    if (error) {
      message.warn(
        intl.formatMessage({
          id: 'login:access'
        }),
        2.5
      );
    }
  }, [error]);
  return (
    <div className="login-container">
      {isExpire ? (
        <div className="expired-text">
          <img src={''} alt="" />
          <div>
            <FormattedMessage id="login:expired" />
          </div>
        </div>
      ) : (
        <>
          <LogoComp />
          <div className="content-container">
            {/* <div className="title">Sign in to your account</div> */}
            <div className="login-wrap">
              <div className="login-box">
                <div className="third-container">
                  <div
                    className="third-item"
                    onClick={() => {
                      const ua: any = window.navigator.userAgent.toLowerCase();
                      if (
                        ua.match(/MicroMessenger/i) == 'micromessenger' ||
                        ua.match(/WeiBo/i) == 'weibo' ||
                        ua.match(/QQ/i) == 'qq'
                      ) {
                        setVisibale(true);
                      } else {
                        thirdLogin(THIRD_URL.GOOGLE);
                      }
                    }}
                  >
                    <img src={GoogleLogo} alt="" />
                    <div className="third-item-label">
                      <FormattedMessage id="login:google" />
                    </div>
                  </div>
                  <div
                    className="third-item"
                    onClick={() => {
                      thirdLogin(THIRD_URL.APPLE);
                    }}
                  >
                    <img src={AppleLogo} alt="" />
                    <div className="third-item-label">
                      <FormattedMessage id="login:apple" />
                    </div>
                  </div>

                  {/* <div
                    className="third-item"
                    onClick={() => {
                      location.href = process.env.NODE_ENV_THIRD_URL_FACEBOOK as string;
                    }}
                  >
                    <img src={FacebookLogo} alt="" />
                  </div> */}

                  <div
                    className="third-item"
                    onClick={() => {
                      thirdLogin(THIRD_URL.MICROSOFT);
                    }}
                  >
                    <img src={MicrosoftLogo} alt="" />
                    <div className="third-item-label">
                      <FormattedMessage id="login:microsoft" />
                    </div>
                  </div>
                </div>

                <Divider className="divider-wrap">
                  <FormattedMessage id="login:title" />
                </Divider>

                <Form
                  form={form}
                  name="registerForm"
                  layout="vertical"
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  autoComplete="off"
                >
                  <Form.Item
                    // label="Email"
                    name="email"
                    validateStatus="success"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: 'login:email:required'
                        })
                      },
                      () => ({
                        validator(_, value) {
                          const reg =
                            /^[A-Za-z0-9._-]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/;
                          if (!value || reg.test(value)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(
                              intl.formatMessage({
                                id: 'login:email:invalid'
                              })
                            )
                          );
                        }
                      })
                    ]}
                  >
                    <Input
                      prefix={<EmailSvg className="email-icon" />}
                      placeholder={intl.formatMessage({
                        id: 'login:email:placeholder'
                      })}
                    />
                  </Form.Item>

                  <Form.Item
                    // label="Password"
                    name="password"
                    validateStatus="success"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: 'login:password:required'
                        })
                      }
                    ]}
                  >
                    <Input.Password
                      prefix={<PasswordSvg className="email-icon" />}
                      placeholder={intl.formatMessage({
                        id: 'login:password:placeholder'
                      })}
                    />
                  </Form.Item>

                  <div className="remember-container">
                    <Checkbox
                      checked={remember}
                      onChange={(e) => {
                        setRemember(e.target.checked);
                      }}
                    >
                      <FormattedMessage id="login:remember" />
                    </Checkbox>

                    <div className="forgetOrSignUp">
                      <a
                        // type="link"
                        onClick={() => {
                          navigate('/reset');
                        }}
                      >
                        <FormattedMessage id="login:forget" />
                      </a>
                      <a
                        type="link"
                        onClick={() => {
                          navigate('/signup');
                        }}
                      >
                        {/*  */}
                      </a>
                    </div>
                  </div>

                  <Form.Item>
                    <Button
                      className="submitBtn"
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                    >
                      <FormattedMessage id="login:signIn" />
                    </Button>
                  </Form.Item>
                </Form>
                <a
                  className="signupBtn"
                  onClick={() => {
                    navigate('/signup');
                  }}
                >
                  <FormattedMessage id="login:cerateAccount" />
                </a>
              </div>
            </div>
          </div>
        </>
      )}

      <Agreement
        text={intl.formatMessage({
          id: 'login:signIn'
        })}
      />
      <WeixinTip visiable={visiable} setVisibale={setVisibale} />
    </div>
  );
};

export default SignIn;
